import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AuditLogEntry {
  id: string;
  created_at: string;
  actor_id: string | null;
  actor_email: string | null;
  actor_role: string | null;
  event_category: 'authentication' | 'authorization' | 'data_access' | 'data_modification' | 'configuration' | 'system' | 'security';
  event_type: string;
  event_severity: 'info' | 'warning' | 'error' | 'critical';
  resource_type: string | null;
  resource_id: string | null;
  resource_name: string | null;
  action: string;
  description: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  changed_fields: string[] | null;
  metadata: Record<string, unknown>;
  is_sensitive: boolean;
}

export interface AuditStats {
  total_events: number;
  events_today: number;
  events_this_week: number;
  critical_events: number;
  unique_actors: number;
}

export interface AuditFilters {
  searchTerm?: string;
  category?: AuditLogEntry['event_category'] | null;
  severity?: AuditLogEntry['event_severity'] | null;
  actorId?: string | null;
  resourceType?: string | null;
  action?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  limit?: number;
  offset?: number;
}

export function useAuditLogs(filters: AuditFilters = {}) {
  return useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_audit_logs', {
        p_search_term: filters.searchTerm || null,
        p_category: filters.category || null,
        p_severity: filters.severity || null,
        p_actor_id: filters.actorId || null,
        p_resource_type: filters.resourceType || null,
        p_action: filters.action || null,
        p_start_date: filters.startDate || null,
        p_end_date: filters.endDate || null,
        p_limit: filters.limit || 100,
        p_offset: filters.offset || 0,
      });

      if (error) throw error;
      return data as AuditLogEntry[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

export function useAuditStats() {
  return useQuery({
    queryKey: ["audit-stats"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_audit_stats');
      
      if (error) throw error;
      return (data as AuditStats[])?.[0] || {
        total_events: 0,
        events_today: 0,
        events_this_week: 0,
        critical_events: 0,
        unique_actors: 0,
      };
    },
    refetchInterval: 30000,
  });
}

export function useAuditLogDetails(logId: string | null) {
  return useQuery({
    queryKey: ["audit-log-detail", logId],
    queryFn: async () => {
      if (!logId) return null;
      
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('id', logId)
        .single();

      if (error) throw error;
      return data as AuditLogEntry;
    },
    enabled: !!logId,
  });
}

// Function to manually log events from client-side
export async function logAuditEvent(params: {
  eventCategory: AuditLogEntry['event_category'];
  eventType: string;
  eventSeverity?: AuditLogEntry['event_severity'];
  resourceType?: string;
  resourceId?: string;
  resourceName?: string;
  action: string;
  description: string;
  metadata?: Record<string, unknown>;
  isSensitive?: boolean;
}) {
  const { data, error } = await supabase.rpc('log_audit_event', {
    p_event_category: params.eventCategory,
    p_event_type: params.eventType,
    p_event_severity: params.eventSeverity || 'info',
    p_resource_type: params.resourceType || null,
    p_resource_id: params.resourceId || null,
    p_resource_name: params.resourceName || null,
    p_action: params.action,
    p_description: params.description,
    p_metadata: (params.metadata || {}) as unknown as Record<string, never>,
    p_is_sensitive: params.isSensitive || false,
  });

  if (error) {
    console.error('Failed to log audit event:', error);
    return null;
  }
  
  return data;
}
